import { useState, useCallback, useRef } from 'react';
import ipService from '../services/ipService';
import { useMessageAlert } from '../components/common/MessageAlert';
import i18n from '../i18n';

/**
 * Custom hook for managing IP addresses
 * Provides CRUD operations and state management for IP addresses
 */
const useIpAddresses = () => {
  const [ipAddresses, setIpAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIp, setSelectedIp] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const lastFetchSignatureRef = useRef(null);
  const inFlightSignatureRef = useRef(null);
  const { showCreate, showUpdate, showDelete } = useMessageAlert();

  /**
   * Fetch IP addresses from the whitelist endpoint.
   * - Accepts options: { skip, limit, active_only, search, force }
   * - Uses a simple request signature dedupe to avoid duplicate network calls.
   */
  const fetchIpAddresses = useCallback(async (options = {}) => {
    const normalizedOptions = {
      skip: options.skip ?? 0,
      limit: options.limit ?? 1000,
      active_only: options.active_only ?? false,
      search: options.search ?? '',
    };

    const signature = JSON.stringify(normalizedOptions);

    if (!options.force) {
      if (inFlightSignatureRef.current === signature) {
        return;
      }

      const lastSignature = lastFetchSignatureRef.current;
      if (lastSignature && lastSignature.signature === signature) {
        const elapsed = Date.now() - lastSignature.timestamp;
        if (elapsed < 200) {
          return;
        }
      }
    }

    inFlightSignatureRef.current = signature;
    try {
      setLoading(true);
      setError(null);
      const data = await ipService.getIpWhitelist(normalizedOptions);

      // Support multiple response shapes: { results: [...] } or { items: [...] } or array
      const list = Array.isArray(data)
        ? data
        : data.results || data.items || data.data || [];

      const count = data?.total ?? data?.count ?? list.length;
      const limit = normalizedOptions.limit || 20;
      setIpAddresses(list);
      setTotalCount(count);
      setTotalPages(Math.max(1, Math.ceil(count / limit)));
    } catch (err) {
      setError(err?.message || String(err));
      console.error('Failed to fetch IP addresses:', err);
    } finally {
      lastFetchSignatureRef.current = { signature, timestamp: Date.now() };
      inFlightSignatureRef.current = null;
      setLoading(false);
    }
  }, []);

  /**
   * Add a single IP address.
   * - Calls the API and appends the result to local state.
   * - Shows a creation notification via MessageAlert.
   */
  const addIpAddress = async (ipData) => {
    try {
      setLoading(true);
      setError(null);
      const newIp = await ipService.addIpAddress(ipData);
  setIpAddresses(prev => [...prev, newIp]);
    try {
      const ipLabel = newIp?.ip_address || newIp?.id || '';
      showCreate(i18n.t('ipAddresses.notifications.added', { ip: ipLabel }));
    } catch (e) {
      // ignore alert errors
    }
      return { success: true, data: newIp };
    } catch (err) {
      setError(err.message);
      console.error('Failed to add IP address:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Add multiple IP addresses in bulk.
   * - Sends bulk payload to API then refreshes the list.
   */
  const addBulkIpAddresses = async (bulkData) => {
    try {
      setLoading(true);
      setError(null);
      const result = await ipService.addBulkIpAddresses(bulkData);
    // Refresh the list to get the newly added IPs
  await fetchIpAddresses({ force: true });
    try {
      const label = `${bulkData?.length || 'Items'}`;
      showCreate(i18n.t('ipAddresses.notifications.bulkAdded', { count: label }));
    } catch (e) {
      // ignore alert errors
    }
      return { success: true, data: result };
    } catch (err) {
      setError(err.message);
      console.error('Failed to add bulk IP addresses:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update an IP address by id.
   * - Merges returned fields into the existing entry to avoid clobbering.
   */
  const updateIpAddress = async (id, ipData) => {
    try {
      setLoading(true);
      setError(null);
      const updatedIp = await ipService.updateIpAddress(id, ipData);
      // Merge returned fields into existing ip to avoid wiping other fields
      setIpAddresses(prev => 
        prev.map(ip => ip.id === id ? { ...ip, ...updatedIp } : ip)
      );
  setSelectedIp(prev => prev && prev.id === id ? { ...prev, ...updatedIp } : updatedIp);
    	try {
    	  const ipStr = updatedIp?.ip_address || ipAddresses.find(ip => ip.id === id)?.ip_address || id;
    	  showUpdate(i18n.t('ipAddresses.notifications.updated', { ip: ipStr }));
    	} catch (e) {
    	  // ignore alert errors
    	}
      return { success: true, data: updatedIp };
    } catch (err) {
      setError(err.message);
      console.error('Failed to update IP address:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Delete an IP address by id.
   * - Removes entry from local state and shows a deletion notification.
   */
  const deleteIpAddress = async (id) => {
    try {
      setLoading(true);
      setError(null);
      await ipService.deleteIpAddress(id);
        // Capture a human-friendly identifier if available before removing
        const deletedIpAddress = ipAddresses.find(ip => ip.id === id)?.ip_address;
        setIpAddresses(prev => prev.filter(ip => ip.id !== id));
        try {
          const ipStr = deletedIpAddress || id;
          showDelete(i18n.t('ipAddresses.notifications.removed', { ip: ipStr }));
        } catch (e) {
          // ignore alert errors
        }
      if (selectedIp?.id === id) {
        setSelectedIp(null);
      }
      return { success: true };
    } catch (err) {
      setError(err.message);
      console.error('Failed to delete IP address:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Activate an IP address (optimistic update)
   * - Immediately flips is_active locally, then reconciles with the API response.
   * - Reverts on failure.
   */
  const activateIpAddress = async (id) => {
    if (!id && id !== 0) {
      const msg = 'activateIpAddress called without a valid id';
      console.error(msg, { id });
      return { success: false, error: msg };
    }

    // Optimistic update: flip is_active immediately, then call API and merge response.
    let prevSnapshot;
    setIpAddresses(prev => {
      prevSnapshot = prev;
      return prev.map(ip => ip.id === id ? { ...ip, is_active: true } : ip);
    });

    try {
      setLoading(true);
      setError(null);
      const updatedIp = await ipService.activateIpAddress(id);
      console.debug('activateIpAddress response', { id, updatedIp, idType: typeof id });
      // Merge partial updates into the existing ip object
      setIpAddresses(prev => 
        prev.map(ip => ip.id === id ? { ...ip, ...updatedIp } : ip)
      );
      // Update selectedIp if it's the same
      setSelectedIp(prev => prev && prev.id === id ? { ...prev, ...updatedIp } : prev);
      // Show update message for activation
      try {
        const ipStr = updatedIp?.ip_address || ipAddresses.find(ip => ip.id === id)?.ip_address || id;
        showUpdate(i18n.t('ipAddresses.notifications.activated', { ip: ipStr }));
      } catch (e) {
        // ignore alert errors
      }
      return { success: true, data: updatedIp };
    } catch (err) {
      // Revert optimistic update on failure
      console.error('Failed to activate IP address:', err);
      if (prevSnapshot) setIpAddresses(prevSnapshot);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Deactivate an IP address (optimistic update)
   * - Same pattern as activation: optimistic flip, API call, reconcile or revert.
   */
  const deactivateIpAddress = async (id) => {
    if (!id && id !== 0) {
      const msg = 'deactivateIpAddress called without a valid id';
      console.error(msg, { id });
      return { success: false, error: msg };
    }

    // Optimistic update: flip is_active immediately, then call API and merge response.
    let prevSnapshot;
    setIpAddresses(prev => {
      prevSnapshot = prev;
      return prev.map(ip => ip.id === id ? { ...ip, is_active: false } : ip);
    });

    try {
      setLoading(true);
      setError(null);
      const updatedIp = await ipService.deactivateIpAddress(id);
      console.debug('deactivateIpAddress response', { id, updatedIp, idType: typeof id });
      // Merge partial updates into the existing ip object
      setIpAddresses(prev => 
        prev.map(ip => ip.id === id ? { ...ip, ...updatedIp } : ip)
      );
      // Update selectedIp if it's the same
      setSelectedIp(prev => prev && prev.id === id ? { ...prev, ...updatedIp } : prev);
      // Show update message for deactivation
        try {
        const ipStr = updatedIp?.ip_address || ipAddresses.find(ip => ip.id === id)?.ip_address || id;
        showUpdate(i18n.t('ipAddresses.notifications.deactivated', { ip: ipStr }));
      } catch (e) {
        // ignore alert errors
      }
      return { success: true, data: updatedIp };
    } catch (err) {
      // Revert optimistic update on failure
      console.error('Failed to deactivate IP address:', err);
      if (prevSnapshot) setIpAddresses(prevSnapshot);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch a single IP address by id from the API.
   * - Optionally shows shared loading state when `withLoading` is true.
   * - Updates `selectedIp` and merges into `ipAddresses` if present.
   */
  const getIpAddressById = useCallback(async (id, options = {}) => {
    const { withLoading = false } = options;

    if (id === undefined || id === null) {
      console.error('getIpAddressById called without a valid id:', id);
      return null;
    }

    if (withLoading) {
      setLoading(true);
    }
    setError(null);

    try {
      const ip = await ipService.getIpAddressById(id);
      if (ip && ip.id !== undefined) {
        setSelectedIp(ip);
        // Merge into list if it already exists (keeps table in sync with freshest data)
        setIpAddresses(prev => {
            const exists = prev.some(item => item.id === ip.id);
            if (!exists) return prev;
            return prev.map(item => item.id === ip.id ? { ...item, ...ip } : item);
          });
      }
      return ip;
    } catch (err) {
      const message = err?.message || i18n.t('hookErrors.ipAddresses.fetchOne');
      setError(message);
      console.error('Failed to fetch IP address by id:', err);
      return null;
    } finally {
      if (withLoading) {
        setLoading(false);
      }
    }
  }, []);

  /**
   * Get IP address from local cache by id (synchronous).
   */
  const getIpById = (id) => {
    return ipAddresses.find(ip => ip.id === id);
  };

  /**
   * Filter IP addresses using simple in-memory filters.
   * - Supported filters: status (boolean), search (substring match on ip_address or description).
   */
  const filterIpAddresses = (filters) => {
    let filtered = [...ipAddresses];

    if (filters.status !== undefined) {
      filtered = filtered.filter(ip => ip.is_active === filters.status);
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(ip => 
        ip.ip_address.toLowerCase().includes(searchLower) ||
        ip.description.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  };

  return {
    // State
    ipAddresses,
    loading,
    error,
    selectedIp,
    totalPages,
    totalCount,
    
    // Actions
    fetchIpAddresses,
    addIpAddress,
    addBulkIpAddresses,
    updateIpAddress,
    deleteIpAddress,
    activateIpAddress,
    deactivateIpAddress,
    setSelectedIp,
    getIpById,
    getIpAddressById,
    filterIpAddresses,
    
    // Utils
    clearError: () => setError(null),
  };
};

export default useIpAddresses;